import React, { useState, useEffect } from "react";
import SearchBox from "../_common/SearchBox";
import ViewProfileModal from "../_common/ViewProfileModal";
import FeedbackPanel from "../_common/FeedbackPanel";
import ApplicationHistory from "../_common/ApplicationHistory";
import ProgressIndicator from "../_common/ProgressIndicator";
import BulkScheduleInterviewModal from "../Interview/BulkScheduleInterviewModal";
import SweetAlert from "react-bootstrap-sweetalert";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { CANDIDATE_STATUS } from "src/_config";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";

interface ScreeningProps {
  jobId?: string;
  currentJob?: any; // Job details passed from parent
  onScheduleInterview?: (candidateId: any, currentPage?: number) => void;
  onSendAIInterview?: (candidateId: any, currentPage?: number, aiInterviewData?: any) => void;
  onMoveToInterview?: (candidateId: any, currentPage?: number) => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

interface Candidate {
  id: number;
  jobseekerId: number;
  name: string;
  appliedFor: string;
  match: number;
  decision: string;
  keyReasons: string[];
  risksConcerns: string[];
  validationQuestions: string[];
  hiringManagerNote: string;
  topKeywords?: string[];
  screeningId?: string;
  risk_type?: string;
  type?: number; // Candidate type: 1 = Applied, 2 = Recommended, 3 = Bulk Upload
  user_type?: string; // User type: "bulk-cv" for bulk uploads
  latestScreeningHistory?: {
    status?: number;
    title?: string;
    description?: string;
  };
  originalScreeningData?: any; // Store complete screening data for ViewProfileModal
}

export default function Screening({
  jobId,
  currentJob,
  onScheduleInterview,
  onSendAIInterview,
  onMoveToInterview,
  currentPage: propCurrentPage,
  onPageChange,
}: ScreeningProps) {
  const { t } = useTranslation("job-details");
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Sync currentPage with parent component
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  // Notify parent when current page changes
  useEffect(() => {
    if (onPageChange && currentPage !== (propCurrentPage || 1)) {
      onPageChange(currentPage);
    }
  }, [currentPage, onPageChange]);
  const [isViewProfileOpen, setIsViewProfileOpen] = useState(false);
  const [candidatesData, setCandidatesData] = useState<any[]>([]); // Store original screening data
  const [selectedJobseekerId, setSelectedJobseekerId] = useState<number | null>(null);
  const [selectedCandidateType, setSelectedCandidateType] = useState<number | undefined>(undefined); // Store selected candidate type
  const [selectedCandidateUserType, setSelectedCandidateUserType] = useState<string | undefined>(undefined); // Store selected candidate user_type
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState<any>(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedCandidateForFeedback, setSelectedCandidateForFeedback] = useState<Candidate | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedCandidateForHistory, setSelectedCandidateForHistory] = useState<Candidate | null>(null);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [candidateToReject, setCandidateToReject] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [screeningPagination, setScreeningPagination] = useState<any>(null);
  const [fullyScreenedStar, setFullyScreenedStar] = useState<number | null>(null);
  const [verifiedStar, setVerifiedStar] = useState<number | null>(null);
  const [potentialStar, setPotentialStar] = useState<number | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [candidateToSchedule, setCandidateToSchedule] = useState<Candidate | null>(null);
  const [selectedDataType, setSelectedDataType] = useState<string | null>(null); // Track selected filter
  const [expandedCandidate, setExpandedCandidate] = useState<string | null>(null);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<number>>(new Set()); // Track selected candidates
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isBulkScheduleModalOpen, setIsBulkScheduleModalOpen] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<number | null>(null);
  const itemsPerPage = 10;

  // API hooks and selector
  const employerJobApi = useEmployerJobApi();
  const screeningApi = useScreeningApi();
  const interviewApi = useInterviewApi();
  const toast = useToaster();

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
    // The useEffect will trigger fetchScreeningData when searchTerm changes
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1); // Reset to first page when clearing search
  };

  // Handler for checkbox selection
  const handleCheckboxChange = (candidateId: number) => {
    setSelectedCandidates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(candidateId)) {
        newSet.delete(candidateId);
      } else {
        newSet.add(candidateId);
      }
      // Update select all state
      setIsSelectAll(newSet.size === candidates.length && candidates.length > 0);
      return newSet;
    });
  };

  // Handler for select all checkbox
  const handleSelectAll = () => {
    if (isSelectAll) {
      setSelectedCandidates(new Set());
      setIsSelectAll(false);
    } else {
      const allCandidateIds = new Set(candidates.map((c) => c.id));
      setSelectedCandidates(allCandidateIds);
      setIsSelectAll(true);
    }
  };

  // Handler for bulk interview scheduling
  const handleBulkScheduleInterview = () => {
    if (selectedCandidates.size === 0) {
      toast.error(t("jobDetails.screening.selectCandidatesFirst"));
      return;
    }
    setIsBulkScheduleModalOpen(true);
  };

  // Handler for bulk AI interview
  const handleBulkSendAIInterview = () => {
    if (selectedCandidates.size === 0) {
      toast.error(t("jobDetails.screening.selectCandidatesFirst"));
      return;
    }

    const selectedCandidatesList = candidates.filter((c) => selectedCandidates.has(c.id));

    // Prepare AI interview data for multiple candidates
    const aiInterviewData = {
      candidates: selectedCandidatesList,
      screeningIds: selectedCandidatesList.map((c) => c.screeningId).filter(Boolean),
    };

    // Call parent callback to open SetValidityModal for bulk AI interview
    if (onSendAIInterview && selectedCandidatesList.length > 0) {
      onSendAIInterview(
        selectedCandidatesList.map((c) => c.id),
        currentPage,
        aiInterviewData,
      );
    }
  };

  // Reset selected candidates when page changes or data refreshes
  useEffect(() => {
    setSelectedCandidates(new Set());
    setIsSelectAll(false);
  }, [currentPage, candidates.length]);

  // Handler for stat card clicks
  const handleStatCardClick = (dataType: string | null) => {
    setSelectedDataType(dataType);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    const maxPages = Math.ceil((screeningPagination?.total || 0) / itemsPerPage);

    if (page >= 1 && page <= maxPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const maxPages = Math.ceil((screeningPagination?.total || 0) / itemsPerPage);

    if (currentPage < maxPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const maxPages = Math.ceil((screeningPagination?.total || 0) / itemsPerPage);

    if (maxPages <= maxVisiblePages) {
      for (let i = 1; i <= maxPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      } else if (currentPage >= maxPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = maxPages - 4; i <= maxPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      }
    }

    return pages;
  };
  // Fetch screening data from API
  const fetchScreeningData = () => {
    if (!jobId) {
      console.warn("Job ID is not available");
      return;
    }

    setLoading(true);

    // Prepare additional parameters similar to TalentScouting
    const additionalParams: any = {};
    if (searchTerm && searchTerm.trim()) {
      additionalParams.search = searchTerm.trim();
    }
    additionalParams.status = [CANDIDATE_STATUS.SCREENED];

    // Add data_type filter if a stat card is selected
    if (selectedDataType) {
      additionalParams.data_type = selectedDataType;
    }

    // Add type filter if selected
    if (selectedTypeFilter !== null) {
      additionalParams.type = [selectedTypeFilter];
    }

    employerJobApi.callGetScreening(
      jobId,
      itemsPerPage,
      (currentPage - 1) * itemsPerPage,
      additionalParams, // Pass search and other params
      (message: string, resp: any) => {
        console.log("Screening data fetched successfully:", resp);

        // Update stat values from API response
        setVerifiedStar(resp.subscribed_count || 0);
        setPotentialStar(resp.potential_match_count || 0);

        // Update fullyScreenedStar based on whether filter is active
        if (selectedDataType === null && resp.pagination) {
          // When showing all screened candidates (no filter), use pagination total
          setFullyScreenedStar(resp.pagination.total || 0);
        } else if (selectedDataType === null) {
          // Fallback if no pagination but no filter
          setFullyScreenedStar(resp.screenings?.length || 0);
        }
        // If a filter is active, keep the previous fullyScreenedStar value (don't update it)

        // Transform and format the screening data
        if (resp && resp.screenings && resp.screenings.length > 0) {
          // Store original screening data for ViewProfileModal
          setCandidatesData(resp.screenings);
          const formattedCandidates = resp.screenings.map((screening: any) => {
            // Extract candidate name from the new jobseeker_details structure
            let candidateName = "";

            if (screening.jobseeker_details && screening.jobseeker_details.length > 0) {
              const jobseekerDetails = screening.jobseeker_details[0];

              if (jobseekerDetails.basic_details && jobseekerDetails.basic_details.length > 0) {
                const basicDetails = jobseekerDetails.basic_details[0];

                if (basicDetails.name && basicDetails.name.trim()) {
                  candidateName = basicDetails.name.trim();
                } else if (basicDetails.first_name && basicDetails.last_name) {
                  candidateName = `${basicDetails.first_name} ${basicDetails.last_name}`.trim();
                } else if (basicDetails.first_name) {
                  candidateName = basicDetails.first_name.trim();
                } else {
                  candidateName = "N/A";
                }
              } else {
                candidateName = "N/A";
              }
            } else {
              candidateName = "N/A";
            }

            return {
              id: screening.id,
              jobseekerId: screening.jobseeker_id,
              name: candidateName,
              appliedFor: currentJob?.job_title || "-",
              match: Math.min(screening.matching_percentage || 0, 95),
              decision: screening.decision || getDecisionByMatch(Math.min(screening.matching_percentage || 0, 95)),
              keyReasons: screening.key_reasons
                ? Array.isArray(screening.key_reasons)
                  ? screening.key_reasons.map((s: string) => s.replace(/^\d+\.\s*/, "").trim()).filter((s: string) => s)
                  : typeof screening.key_reasons === "string"
                    ? screening.key_reasons
                        .split("\n")
                        .map((s: string) => s.replace(/^\d+\.\s*/, "").trim())
                        .filter((s: string) => s)
                    : []
                : [],
              risksConcerns: screening.risks_concerns
                ? Array.isArray(screening.risks_concerns)
                  ? screening.risks_concerns
                      .map((w: string) => w.replace(/^\d+\.\s*/, "").trim())
                      .filter((w: string) => w)
                  : typeof screening.risks_concerns === "string"
                    ? screening.risks_concerns
                        .split("\n")
                        .map((w: string) => w.replace(/^\d+\.\s*/, "").trim())
                        .filter((w: string) => w)
                    : []
                : [],
              validationQuestions: screening.validation_questions
                ? Array.isArray(screening.validation_questions)
                  ? screening.validation_questions
                      .map((q: string) => q.replace(/^\d+\.\s*/, "").trim())
                      .filter((q: string) => q)
                  : typeof screening.validation_questions === "string"
                    ? screening.validation_questions
                        .split("\n")
                        .map((q: string) => q.replace(/^\d+\.\s*/, "").trim())
                        .filter((q: string) => q)
                    : []
                : [],
              hiringManagerNote: screening.hiring_manager_note || "",
              topKeywords: screening.top_keywords
                ? Array.isArray(screening.top_keywords)
                  ? screening.top_keywords
                  : []
                : [],
              screeningId: screening.id,
              type: screening.type, // Capture candidate type
              user_type: screening.user_type || undefined,
              latestScreeningHistory: screening.latest_screening_history || null,
              // Store complete screening data for ViewProfileModal
              originalScreeningData: screening,
            };
          });

          setCandidates(formattedCandidates);
        } else {
          // Clear candidates data when no screenings are returned
          setCandidatesData([]);
          setCandidates([]);
        }

        if (resp && resp.pagination) {
          setScreeningPagination(resp.pagination);
        } else {
          // Clear pagination when no data is returned
          setScreeningPagination(null);
        }

        setLoading(false);
      },
      (error: string) => {
        console.error("Error fetching screening data:", error);
        // Clear candidates data on error
        setCandidatesData([]);
        setCandidates([]);
        setScreeningPagination(null);
        // toast.error(error);
        setLoading(false);
      },
    );
  };

  // useEffect to call API on component mount and when dependencies change
  useEffect(() => {
    if (jobId) {
      fetchScreeningData();
    }
  }, [currentPage, jobId, searchTerm, selectedDataType, selectedTypeFilter]);

  const getDecisionByMatch = (match: number): string => {
    var decision = "";
    match = Math.round(match);
    if (match >= 80 && match <= 95) decision = "Strong Fit";
    if (match >= 55 && match <= 79) decision = "Potential Fit";
    if (match >= 60 && match <= 70) decision = "Overqualified";
    if (match >= 0 && match <= 54) decision = "Not a Fit";
    return decision;
  };

  const getMatchColor = (match: number) => {
    const decision = getDecisionByMatch(match);
    // Return lighter colors for better visibility while maintaining decision badge style
    var color = "";
    if (decision === "Strong Fit") color = "#10b981"; // Lighter green (emerald-500)
    if (decision === "Potential Fit") color = "#f59e0b"; // Lighter amber (amber-500)
    if (decision === "Overqualified") color = "#6366f1"; // Lighter indigo (indigo-500)
    if (decision === "Not a Fit") color = "#ef4444"; // Lighter red (red-500)
    // return "#10b981"; // Default lighter green
    return color;
  };

  const getDecisionDescription = (decision: string): string => {
    const normalizedDecision = decision.toLowerCase().trim();

    if (normalizedDecision.includes("strong fit")) {
      return "Candidate is well-aligned with the role and context.";
    } else if (normalizedDecision.includes("potential fit")) {
      return "Candidate may succeed, with identifiable risks that must be validated.";
    } else if (normalizedDecision.includes("not a fit")) {
      return "Candidate is unlikely to succeed in this role as defined.";
    } else if (normalizedDecision.includes("overqualified")) {
      return "Candidate exceeds role requirements and may disengage, exit early, or misalign with expectations.";
    }

    return "";
  };

  const handleSendAIInterview = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate) return;

    // Prepare AI interview data
    const aiInterviewData = {
      candidate,
      screeningId: candidate.screeningId,
    };

    // Call parent callback to open SetValidityModal
    // The actual AI interview logic will be executed after validity is set
    if (onSendAIInterview) {
      onSendAIInterview(candidateId, currentPage, aiInterviewData);
    }
  };

  const handleScheduleInterview = (candidateId: any) => {
    console.log("Schedule Interview for candidate:", candidateId);
    const candidate = candidates.find((c) => c.id === candidateId);
    console.log("Found candidate:", candidate);
    // Commented: switching to Interview tab via parent callback.
    // if (onScheduleInterview) {
    //   onScheduleInterview(candidateId, currentPage);
    // }
    if (candidate) {
      setCandidateToSchedule(candidate);
      setIsScheduleModalOpen(true);
    }
  };

  const handleViewStatus = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (candidate && candidate.screeningId) {
      // Open the ApplicationHistory panel - it will handle the API call internally
      setSelectedCandidateForHistory(candidate);
      setIsHistoryOpen(true);
    } else {
      console.warn("No screening ID found for candidate:", candidateId);
      toast.error("Unable to fetch history: No screening ID found");
    }
  };

  const handleAddNote = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (candidate) {
      setSelectedCandidateForFeedback(candidate);
      setIsFeedbackOpen(true);
    }
  };

  const handleReject = (candidateId: any) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (candidate) {
      setCandidateToReject(candidate);
      setShowRejectConfirm(true);
    }
  };

  const handleConfirmReject = () => {
    if (candidateToReject) {
      // Call addhistory API with rejection parameters
      const payload = {
        // ...(userDetails?.user_type != "member" ? { member_id: userDetails?.user?.id } : {}),
        screening_id: candidateToReject.screeningId,
        application_type: 1,
        status: 2,
        title: t("jobDetails.screening.apiPayload.rejectTitle"),
        description: t("jobDetails.screening.apiPayload.rejectDescription"),
      };

      screeningApi.callAddFeedback(
        payload,
        (message: string, resp: any) => {
          console.log("Candidate rejection recorded successfully:", resp);
          toast.success(t("jobDetails.screening.rejectSuccess"));

          // Optionally refresh the screening data
          fetchScreeningData();
        },
        (error: string) => {
          console.error("Error recording candidate rejection:", error);
          toast.error(t("jobDetails.screening.rejectError") + ": " + error);
        },
      );

      // Also update candidate status to REJECTED
      screeningApi.callUpdateStatus(
        {
          screening_id: candidateToReject?.screeningId,
          status: CANDIDATE_STATUS.REJECTED,
        },
        (message: string, resp: any) => {
          // status updated
        },
        (error: string) => {
          // silent fail to not block UX
        },
      );

      // Close the modal
      setShowRejectConfirm(false);
      setCandidateToReject(null);
    }
  };

  const handleCancelReject = () => {
    setShowRejectConfirm(false);
    setCandidateToReject(null);
  };

  const handleViewProfile = (candidate: Candidate) => {
    // Use the originalScreeningData if available, otherwise fallback to candidatesData lookup
    const originalScreeningData =
      candidate.originalScreeningData || candidatesData.find((screening) => screening.id === candidate.id);

    if (originalScreeningData && originalScreeningData.jobseeker_details) {
      setSelectedJobseekerId(candidate.jobseekerId);
      setSelectedCandidateType(candidate.type); // Store candidate type
      setSelectedCandidateUserType(candidate.user_type); // Store candidate user_type

      const jobseekerDetails = originalScreeningData.jobseeker_details[0];
      const basicDetails = jobseekerDetails.basic_details?.[0] || {};

      // Get current designation from experience first, then fallback to basic details
      const currentDesignation =
        jobseekerDetails?.experience?.[0]?.designation || basicDetails?.current_designation || "-";

      // Map the data structure to match ViewProfileModal expectations
      const detailsToSet = {
        name: basicDetails.name || candidate.name,
        avatar: originalScreeningData.document_full_path?.profile_pic || null,
        currentDesignation: currentDesignation,
        currentLocation: basicDetails.current_location || "",
        totalExperienceYear: basicDetails.total_experience_year || "",
        gender: basicDetails.gender_id ? (basicDetails.gender_id === 1 ? "Male" : "Female") : "-",
        currentSalary: basicDetails.current_salary || "",
        currentSalaryCurrency: basicDetails.current_salary_currency || "",
        skills: Array.isArray(basicDetails.key_skills) ? basicDetails.key_skills.join(", ") : "",
        profileSummary: basicDetails.profile_summary || "",
        languages: basicDetails?.known_languages || "-",
        // Array.isArray(jobseekerDetails.languages)
        //   ? jobseekerDetails.languages.map((lang: any) => lang.language_name || lang).join(", ")
        //   : "",
        // Map basic details for file downloads
        basicDetails: {
          ...basicDetails,
          resume_file: originalScreeningData.document_full_path?.system_resume_file || null,
          video_file: originalScreeningData.document_full_path?.video_file || null,
          jobseeker_id: candidate.jobseekerId,
        },
        // Document full path with original resume_file
        document_full_path: originalScreeningData.document_full_path || null,
        // Map employment details
        employmentDetails: jobseekerDetails.experience || [],
        // Map education details
        educationDetails: jobseekerDetails.education || [],
        // Map course details
        courseDetails: jobseekerDetails.courses || [],
        // Map project details
        projectDetails: jobseekerDetails.projects || [],
        // Map certificate details
        certificateDetails: jobseekerDetails.certifications || [],
        // Public profile information
        jobseeker_public_profiles: jobseekerDetails.jobseeker_public_profiles || [],
      };
      console.log("Setting candidate details for ViewProfileModal:", detailsToSet);
      setSelectedCandidateDetails(detailsToSet);
    } else {
      // Fallback - just pass jobseekerId for API fetch
      setSelectedJobseekerId(candidate.jobseekerId);
      setSelectedCandidateType(candidate.type); // Store candidate type
      setSelectedCandidateUserType(candidate.user_type); // Store candidate user_type
      setSelectedCandidateDetails(null);
    }
    setIsViewProfileOpen(true);
  };

  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
    setSelectedCandidateForFeedback(null);
  };

  const handleCloseHistory = () => {
    setIsHistoryOpen(false);
    setSelectedCandidateForHistory(null);
  };

  const handleCandidateToggle = (candidateId: number) => {
    setExpandedCandidate(expandedCandidate === candidateId.toString() ? null : candidateId.toString());
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setCandidateToSchedule(null);
  };

  const handleCloseBulkScheduleModal = () => {
    setIsBulkScheduleModalOpen(false);
  };

  const handleScheduleConfirm = (scheduleData: any) => {
    console.log("Interview scheduled with data:", scheduleData);
    console.log("For candidate:", candidateToSchedule);

    // Step 1: Record "Candidate Screened" history FIRST
    const screenedPayload = {
      screening_id: candidateToSchedule?.screeningId,
      application_type: 1,
      status: 0,
      title: "Candidate Screened",
      description: `- Match: ${candidateToSchedule?.match || 0}%\n- Decision: ${candidateToSchedule?.decision || "-"}\n- Key reasons: ${candidateToSchedule?.keyReasons?.length ? candidateToSchedule.keyReasons.join(", ") : "-"}`,
    };

    screeningApi.callAddFeedback(
      screenedPayload,
      (screenedMessage: string, screenedResp: any) => {
        console.log("Candidate Screened history recorded successfully:", screenedResp);

        // Step 2: Call Schedule Request API
        const schedulePayload = {
          job_id: jobId,
          member_id: scheduleData.member_id,
          interview_schedule: scheduleData.interviewSchedule,
          title: "Live Interview Scheduled",
          description: "Interview has been scheduled with team member",
          screening_id: candidateToSchedule?.screeningId,
          member_available_time: scheduleData.memberAvailableTime,
        };

        (interviewApi.callScheduleRequest as any)(
          schedulePayload,
          (scheduleMessage: string, scheduleResp: any) => {
            console.log("Interview schedule request successful:", scheduleResp);
            toast.success(scheduleMessage);

            // Step 3: Record "Live Interview Scheduled" history
            const feedbackPayload = {
              screening_id: candidateToSchedule?.screeningId,
              application_type: 1,
              status: 9,
              title: t("jobDetails.screening.apiPayload.scheduleInterviewTitle") || "Live Interview Scheduled",
              description:
                t("jobDetails.screening.apiPayload.scheduleInterviewDescription") ||
                "Live interview has been scheduled with the candidate.",
            };

            screeningApi.callAddFeedback(
              feedbackPayload,
              (message: string, resp: any) => {
                console.log("Interview schedule feedback recorded successfully:", resp);

                // Step 4: Update candidate status
                screeningApi.callUpdateStatus(
                  {
                    screening_id: candidateToSchedule?.screeningId,
                    status: CANDIDATE_STATUS.SCHEDULE_INTERVIEW,
                  },
                  (message: string, resp: any) => {
                    console.log("Candidate status updated to Scheduled Interview:", resp);
                    // Navigate to Interview tab after successful scheduling
                    if (onMoveToInterview && candidateToSchedule?.screeningId) {
                      onMoveToInterview(candidateToSchedule.screeningId, currentPage);
                    }
                  },
                  (error: string) => {
                    toast.error(t("jobDetails.screening.statusUpdateError") + ": " + error);
                  },
                );
              },
              (error: string) => {
                console.error("Error recording interview schedule feedback:", error);
              },
            );
          },
          (error: string) => {
            console.error("Error scheduling interview:", error);
            toast.error("Failed to schedule interview: " + error);
          },
        );
      },
      (error: string) => {
        console.error("Error recording Candidate Screened history:", error);
        toast.error("Failed to record screening history: " + error);
      },
    );
  };

  const handleBulkScheduleConfirm = (scheduleData: any) => {
    console.log("Bulk interview scheduled with data:", scheduleData);

    const selectedCandidatesList = candidates.filter((c) => selectedCandidates.has(c.id));
    console.log("Scheduling for candidates:", selectedCandidatesList);

    // Collect all screening IDs for bulk operation
    const screeningIds = selectedCandidatesList.map((candidate) => candidate.screeningId);

    // Step 1: Record "Candidate Screened" history for all candidates FIRST
    const screenedPayload = {
      screening_id: screeningIds,
      application_type: 1,
      status: 0,
      title: "Candidate Screened",
      description: selectedCandidatesList.map(
        (c) =>
          `- Match: ${c.match || 0}%\n- Decision: ${c.decision || "-"}\n- Key reasons: ${c.keyReasons?.length ? c.keyReasons.join(", ") : "-"}`,
      ),
    };

    screeningApi.callAddFeedback(
      screenedPayload,
      (screenedMessage: string, screenedResp: any) => {
        console.log("Bulk Candidate Screened history recorded successfully:", screenedResp);

        // Step 2: Call Schedule Request API
        const schedulePayload = {
          job_id: jobId,
          member_id: scheduleData.member_id,
          title: "Live Interview Scheduled",
          description: "Interview has been scheduled with team member",
          screening_id: screeningIds,
          member_available_time: scheduleData.memberAvailableTime,
        };

        (interviewApi.callScheduleRequest as any)(
          schedulePayload,
          (scheduleMessage: string, scheduleResp: any) => {
            console.log("Bulk schedule request successful:", scheduleResp);
            toast.success(scheduleMessage || t("jobDetails.screening.bulkScheduleSuccess"));

            // Step 3: Record "Interview Scheduled" history
            const feedbackPayload = {
              screening_id: screeningIds,
              application_type: 1,
              status: 9,
              title: t("jobDetails.screening.apiPayload.scheduleInterviewTitle") || "Live Interview Scheduled",
              description:
                t("jobDetails.screening.apiPayload.scheduleInterviewDescription") ||
                "Live interview has been scheduled with the candidate.",
            };

            screeningApi.callAddFeedback(
              feedbackPayload,
              (message: string, resp: any) => {
                console.log("Bulk interview schedule feedback recorded successfully:", resp);

                // Step 4: Update candidate status
                screeningApi.callUpdateStatus(
                  {
                    screening_id: screeningIds,
                    status: CANDIDATE_STATUS.SCHEDULE_INTERVIEW,
                  },
                  (message: string, resp: any) => {
                    console.log("Bulk candidate status updated to Scheduled Interview:", resp);
                    setSelectedCandidates(new Set());
                    setIsSelectAll(false);

                    // Navigate to Interview tab after successful bulk scheduling
                    if (onMoveToInterview) {
                      onMoveToInterview(null, currentPage);
                    }
                  },
                  (error: string) => {
                    console.error("Error updating bulk candidate status:", error);
                    toast.error(`${t("jobDetails.screening.statusUpdateError")}: ${error}`);
                    setSelectedCandidates(new Set());
                    setIsSelectAll(false);
                  },
                );
              },
              (error: string) => {
                console.error("Error recording bulk feedback:", error);
                toast.error(`${t("jobDetails.screening.feedbackError")}: ${error}`);
                setSelectedCandidates(new Set());
                setIsSelectAll(false);
              },
            );
          },
          (error: string) => {
            console.error("Error bulk scheduling interviews:", error);
            toast.error("Failed to schedule interviews: " + error);
            setSelectedCandidates(new Set());
            setIsSelectAll(false);
          },
        );
      },
      (error: string) => {
        console.error("Error recording bulk Candidate Screened history:", error);
        toast.error("Failed to record screening history: " + error);
        setSelectedCandidates(new Set());
        setIsSelectAll(false);
      },
    );
  };

  // Removed useEffect for fullyScreenedStar - now updated directly in API response handler

  return (
    <div className="screening-wrapper">
      {/* Header Section */}
      <div className="screening-header">
        <div className="header-left-ai-job">
          <div className="job-overview-section">
            <h1 className="job-overview-title">{currentJob?.job_title}</h1>
            <p className="job-overview-subtitle">{t("jobDetails.screening.subtitle")}</p>
          </div>
        </div>
        {/* Only show search when there are candidates or search is active or loading */}
        {!(!loading && (!candidates || candidates.length === 0) && !selectedDataType && !searchTerm) && (
          <div className="header-search">
            <SearchBox
              placeholder={t("jobDetails.screening.searchPlaceholder")}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
        )}
      </div>
      {/* Conditional Content: Show progress card if no screening data, otherwise show stats and candidates */}
      {/* {!loading && (!candidates || candidates.length === 0) && !selectedDataType ? (
        <ProgressIndicator stage="screening" />
      ) : ( */}
      <>
        {/* Stats Cards */}
        <div className="screening-stats-grid">
          <div
            className={`screening-stat-card screening-stat-red ${selectedDataType === null ? "active" : ""}`}
            onClick={() => handleStatCardClick(null)}
            style={{ cursor: "pointer" }}
          >
            <div className="screening-stat-content">
              <h3 className="screening-stat-title">{t("jobDetails.screening.fullyScreened")}</h3>
              <span className="screening-stat-number">{fullyScreenedStar || 0}</span>
            </div>
            <div className="screening-stat-icon screening-stat-icon-red">
              <i className="fa fa-file-text"></i>
            </div>
          </div>
          <div
            className={`screening-stat-card screening-stat-blue ${
              selectedDataType === "potential_match" ? "active" : ""
            }`}
            onClick={() => handleStatCardClick("potential_match")}
            style={{ cursor: "pointer" }}
          >
            <div className="screening-stat-content">
              <h3 className="screening-stat-title">{t("jobDetails.screening.potentialStar")}</h3>
              <span className="screening-stat-number">{potentialStar || 0}</span>
            </div>
            <div className="screening-stat-icon screening-stat-icon-blue">
              <i className="fa fa-file-text"></i>
            </div>
          </div>
          <div
            className={`screening-stat-card screening-stat-green ${selectedDataType === "subscribed" ? "active" : ""}`}
            onClick={() => handleStatCardClick("subscribed")}
            style={{ cursor: "pointer" }}
          >
            <div className="screening-stat-content">
              <h3 className="screening-stat-title">{t("jobDetails.screening.verifiedStar")}</h3>
              <span className="screening-stat-number">{verifiedStar || 0}</span>
            </div>
            <div className="screening-stat-icon screening-stat-icon-green">
              <i className="fa fa-check-square"></i>
            </div>
          </div>
        </div>

        {/* Candidate Type Filter */}
        <div className="talent-scouting-type-filter" style={{ display: "flex", gap: "8px", padding: "12px 0" }}>
          {[
            { label: t("jobDetails.screening.typeFilter.all"), value: null },
            { label: t("jobDetails.screening.typeFilter.aiRecommendation"), value: 2 },
            { label: t("jobDetails.screening.typeFilter.bulkCvUpload"), value: 3 },
            { label: t("jobDetails.screening.typeFilter.applied"), value: 1 },
          ].map((filter) => (
            <button
              key={String(filter.value)}
              className={`filter-tab-btn ${selectedTypeFilter === filter.value ? "active" : ""}`}
              style={{
                padding: "6px 14px",
                borderRadius: "20px",
                border: selectedTypeFilter === filter.value ? "1px solid #a5c5e5" : "1px solid #d0d5dd",
                background: selectedTypeFilter === filter.value ? "#a5c5e5" : "#fff",
                color: selectedTypeFilter === filter.value ? "#fff" : "#344054",
                fontSize: "13px",
                cursor: "pointer",
                fontWeight: selectedTypeFilter === filter.value ? 600 : 400,
              }}
              onClick={() => {
                setSelectedTypeFilter(filter.value);
                setCurrentPage(1);
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Hiring Manager Assessment Heading */}
        {!loading && candidates && candidates.length > 0 && (
          <div className="job-overview-section" style={{ marginTop: "30px", marginBottom: "10px" }}>
            <h1 className="job-overview-title">AI Hiring Manager Evaluation</h1>
            <p className="job-overview-subtitle">
              Based on job context, requirements, and real-world role expectations
            </p>
          </div>
        )}

        {/* Select All and Bulk Actions Bar */}
        {!loading && candidates && candidates.length > 0 && (
          <div className="screening-bulk-actions-bar">
            <div className="screening-select-all">
              <input
                type="checkbox"
                id="select-all-candidates"
                checked={isSelectAll}
                onChange={handleSelectAll}
                className="screening-checkbox"
              />
              <label htmlFor="select-all-candidates" className="screening-select-all-label">
                {t("jobDetails.screening.selectAll")} ({selectedCandidates.size} {t("jobDetails.screening.selected")})
              </label>
            </div>

            <div className="screening-bulk-actions">
              {canAccessMenu("create_ai_interview") && (
                <button
                  className="screening-btn screening-btn-send-ai"
                  onClick={handleBulkSendAIInterview}
                  disabled={selectedCandidates.size === 0}
                >
                  <i className="fa fa-paper-plane"></i>
                  {t("jobDetails.screening.sendAIInterview")}
                </button>
              )}
              <button
                className="screening-btn screening-btn-schedule"
                onClick={handleBulkScheduleInterview}
                disabled={selectedCandidates.size === 0}
              >
                <i className="fa fa-calendar"></i>
                {t("jobDetails.screening.scheduleInterview")}
              </button>
            </div>
          </div>
        )}

        {/* Candidates List */}
        <div className="screening-candidates-section">
          {loading ? (
            <div className="screening-loading" style={{ textAlign: "center", padding: "60px 20px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #3498db",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                <span style={{ fontSize: "16px", color: "#666" }}>{t("jobDetails.screening.loadingData")}</span>
              </div>
              <p style={{ color: "#999", fontSize: "14px", margin: "0" }}>{t("jobDetails.screening.pleaseWait")}</p>
            </div>
          ) : candidates?.length > 0 ? (
            candidates.map((candidate) => (
              <div key={candidate.id} className="screening-candidate-card">
                <div
                  className="screening-candidate-header"
                  onClick={() => handleCandidateToggle(candidate.id)}
                  style={{ cursor: "pointer" }}
                >
                  {/* Checkbox for candidate selection */}
                  <div
                    className="screening-candidate-checkbox"
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginRight: "16px" }}
                  >
                    <input
                      type="checkbox"
                      id={`candidate-checkbox-${candidate.id}`}
                      checked={selectedCandidates.has(candidate.id)}
                      onChange={() => handleCheckboxChange(candidate.id)}
                      className="screening-checkbox"
                    />
                  </div>

                  {/* Avatar */}
                  <div className="screening-candidate-avatar" style={{ marginRight: "16px" }}>
                    <img
                      src={
                        candidate.originalScreeningData?.document_full_path?.profile_pic || "/images/user-avatar.jpg"
                      }
                      alt={candidate.name}
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/images/user-avatar.jpg";
                      }}
                    />
                  </div>

                  <div className="screening-candidate-info">
                    <div className="screening-candidate-details">
                      <h3
                        className="screening-candidate-name"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProfile(candidate);
                        }}
                        style={{ cursor: "pointer", color: "#3b82f6", width: "fit-content" }}
                      >
                        {candidate.name}
                      </h3>
                      <p className="screening-candidate-position">
                        {t("jobDetails.screening.appliedFor")} {candidate.appliedFor}
                      </p>
                    </div>
                    {candidate.topKeywords && candidate.topKeywords.length > 0 && (
                      <div className="screening-top-keywords d-none" style={{ marginTop: "20px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {candidate.topKeywords.map((keyword, index) => (
                            <span
                              key={index}
                              style={{
                                display: "inline-block",
                                padding: "4px 10px",
                                backgroundColor: "#eff6ff",
                                color: "#1e40af",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "500",
                                border: "1px solid #bfdbfe",
                              }}
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Decision Section - Moved to header */}
                    <div className="screening-decision-header">
                      <span
                        className={`screening-decision-badge screening-decision-${getDecisionByMatch(candidate.match).toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {getDecisionByMatch(candidate.match)}
                        {getDecisionDescription(getDecisionByMatch(candidate.match)) && (
                          <p className="screening-decision-description">
                            {getDecisionDescription(getDecisionByMatch(candidate.match))}
                          </p>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="screening-candidate-right-section">
                    <div className="screening-match-score">
                      <div className="screening-match-circle">
                        <svg width="70" height="70" viewBox="0 0 70 70">
                          <circle cx="35" cy="35" r="30" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                          <circle
                            cx="35"
                            cy="35"
                            r="30"
                            fill="none"
                            stroke={getMatchColor(candidate.match)}
                            strokeWidth="6"
                            strokeDasharray={`${candidate.match * 1.88} 188.5`}
                            strokeLinecap="round"
                            transform="rotate(-90 35 35)"
                          />
                        </svg>
                        <div className="screening-match-text">
                          <span
                            className="screening-match-percentage"
                            style={{ color: getMatchColor(candidate.match) }}
                          >
                            {Math.round(candidate.match)}%
                          </span>
                          <small className="screening-match-label d-none">Match</small>
                        </div>
                      </div>
                    </div>
                    <button className={`expand-btn ${expandedCandidate === candidate.id.toString() ? "expanded" : ""}`}>
                      <i className="fa fa-chevron-down"></i>
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedCandidate === candidate.id.toString() && (
                  <div className="screening-candidate-content">
                    {/* Strengths and Weaknesses */}
                    <div className="screening-strengths-weaknesses">
                      {candidate?.keyReasons && candidate.keyReasons.length > 0 && (
                        <div className="screening-strengths">
                          <h4 className="screening-section-title">{t("jobDetails.screening.keyReasons")}</h4>
                          <ul className="screening-list" style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                            {candidate.keyReasons.map((reason, index) => (
                              <li key={index} className="screening-list-item" style={{ display: "list-item" }}>
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {candidate?.risksConcerns && candidate.risksConcerns.length > 0 && (
                        <div className="screening-weaknesses">
                          <h4 className="screening-section-title">{t("jobDetails.screening.risksConcerns")}</h4>

                          {/* Warning Badge */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "12px",
                              marginBottom: "12px",
                              padding: "8px 12px",
                              backgroundColor: "#fff3cd",
                              border: "1px solid #ffc107",
                              borderRadius: "4px",
                              color: "#856404",
                              width: "fit-content",
                            }}
                          >
                            <i className="fa fa-info-circle" style={{ fontSize: "16px" }}></i>
                            <span style={{ fontSize: "14px", fontWeight: "500" }}>
                              {candidate?.risk_type == "real_risk"
                                ? "Risk"
                                : t("jobDetails.screening.needsConfirmation")}
                            </span>
                          </div>
                          <ul className="screening-list" style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                            {candidate.risksConcerns.map((concern, index) => (
                              <li key={index} className="screening-list-item" style={{ display: "list-item" }}>
                                {concern}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Validation Questions Section */}
                    {candidate?.validationQuestions && candidate.validationQuestions.length > 0 && (
                      <div className="screening-validation-questions">
                        <h4 className="screening-section-title">{t("jobDetails.screening.validationQuestions")}</h4>
                        <ul className="screening-list" style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                          {candidate.validationQuestions.map((question, index) => (
                            <li key={index} className="screening-list-item" style={{ display: "list-item" }}>
                              {question}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Hiring Manager Note */}
                    {candidate?.hiringManagerNote && (
                      <div className="screening-hiring-note">
                        <h4 className="screening-section-title">{t("jobDetails.screening.hiringManagerNote")}</h4>
                        <p className="screening-note-text">{candidate.hiringManagerNote}</p>
                      </div>
                    )}

                    <div className="screening-candidate-actions">
                      <div className="screening-action-buttons-left">
                        <button
                          className="screening-btn screening-btn-view-status"
                          onClick={() => handleViewStatus(candidate.id)}
                        >
                          {t("jobDetails.screening.viewStatus")}
                        </button>
                        <button
                          className="screening-btn screening-btn-add-note"
                          onClick={() => handleAddNote(candidate.id)}
                        >
                          <i className="fa fa-edit"></i>
                          {t("jobDetails.screening.addNote")}
                        </button>
                        {/* Hide reject button if candidate is already rejected (status = 2) */}
                        {candidate.latestScreeningHistory?.status == 2 ? (
                          <button className="screening-btn screening-btn-rejected" disabled>
                            <i className="fa fa-times-circle"></i>
                            {t("jobDetails.screening.rejected")}
                          </button>
                        ) : candidate.latestScreeningHistory?.status == 3 ? (
                          <button className="screening-btn screening-btn-hired" disabled>
                            <i className="fa fa-check-circle"></i>
                            {t("jobDetails.screening.hired")}
                          </button>
                        ) : (
                          <button
                            className="screening-btn screening-btn-reject"
                            onClick={() => handleReject(candidate.id)}
                          >
                            <i className="fa fa-times"></i>
                            {t("jobDetails.screening.reject")}
                          </button>
                        )}
                      </div>

                      <div className="screening-action-buttons-right">
                        {canAccessMenu("create_ai_interview") && (
                          <button
                            className="screening-btn screening-btn-send-ai"
                            onClick={() => handleSendAIInterview(candidate.id)}
                          >
                            <i className="fa fa-paper-plane"></i>
                            {t("jobDetails.screening.sendAIInterview")}
                          </button>
                        )}
                        <button
                          className="screening-btn screening-btn-schedule"
                          onClick={() => handleScheduleInterview(candidate.id)}
                        >
                          <i className="fa fa-calendar"></i>
                          {t("jobDetails.screening.scheduleInterview")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="screening-no-candidates">
              <p>{t("jobDetails.screening.noCandidates")}</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && candidates.length > 0 && Math.ceil((screeningPagination?.total || 0) / itemsPerPage) > 1 ? (
          <div className="pagination">
            <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
              {t("jobDetails.screening.prev")}
            </button>
            <div className="page-numbers">
              {getPageNumbers().map((page, index) =>
                page === "..." ? (
                  <span key={`ellipsis-${index}`}>...</span>
                ) : (
                  <button
                    key={page}
                    className={`page-num ${currentPage === page ? "active" : ""}`}
                    onClick={() => handlePageChange(page as number)}
                  >
                    {page}
                  </button>
                ),
              )}
            </div>
            <button
              className="page-btn"
              onClick={handleNextPage}
              disabled={currentPage === Math.ceil((screeningPagination?.total || 0) / itemsPerPage)}
            >
              {t("jobDetails.screening.next")}
            </button>
          </div>
        ) : null}
      </>
      {/* )} */}
      {/* View Profile Modal */}
      <ViewProfileModal
        isOpen={isViewProfileOpen}
        onClose={() => setIsViewProfileOpen(false)}
        jobId={jobId}
        jobseekerId={selectedJobseekerId || undefined}
        candidateType={selectedCandidateType}
        user_type={selectedCandidateUserType}
      />
      {/* Feedback Panel */}
      <FeedbackPanel
        candidateName={selectedCandidateForFeedback?.name || ""}
        screeningId={selectedCandidateForFeedback?.screeningId || ""}
        isOpen={isFeedbackOpen}
        onClose={handleCloseFeedback}
      />
      {/* Application History Panel */}
      <ApplicationHistory
        candidateName={selectedCandidateForHistory?.name || ""}
        screeningId={selectedCandidateForHistory?.screeningId}
        isOpen={isHistoryOpen}
        onClose={handleCloseHistory}
      />
      {/* Reject Confirmation Modal */}
      {showRejectConfirm && candidateToReject && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={t("jobDetails.screening.confirmReject.yesReject")}
          confirmBtnBsStyle="danger"
          cancelBtnText={t("jobDetails.screening.confirmReject.cancel")}
          cancelBtnBsStyle="default"
          title={t("jobDetails.screening.confirmReject.title")}
          onConfirm={handleConfirmReject}
          onCancel={handleCancelReject}
        >
          {t("jobDetails.screening.confirmReject.message")} <strong>{candidateToReject.name}</strong>?
          <br />
          <br />
          {t("jobDetails.screening.confirmReject.warning")}
        </SweetAlert>
      )}
      {/* Schedule Interview Modal */}
      <BulkScheduleInterviewModal
        isOpen={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        candidates={
          candidateToSchedule
            ? [
                {
                  id:
                    typeof candidateToSchedule.id === "number"
                      ? candidateToSchedule.id
                      : parseInt(String(candidateToSchedule.id || "0")) || 0,
                  name: candidateToSchedule.name || "",
                  screeningId: String(candidateToSchedule.screeningId || candidateToSchedule.id || ""),
                },
              ]
            : []
        }
        jobId={jobId || ""}
        onSchedule={handleScheduleConfirm}
      />
      {/* Bulk Schedule Interview Modal */}
      <BulkScheduleInterviewModal
        isOpen={isBulkScheduleModalOpen}
        onClose={handleCloseBulkScheduleModal}
        candidates={candidates.filter((c) => selectedCandidates.has(c.id))}
        jobId={jobId || ""}
        onSchedule={handleBulkScheduleConfirm}
      />
    </div>
  );
}
